// ─────────────────────────────────────────────
// Oike · Mi marca — visual identities of Ale's personal brand, defined once and reused by every format that adopts
// them. Pure (no browser APIs): shared by the screens, the PDF builder, owner-space and the tests.
//
// Identity A «Pizarra» (approved by Ale on 04-11-2026 as the base of her brand): as if she explained an idea on a
// chalkboard. Expressive handwritten titles, simple legible text, turquoise, coral and yellow accents; strokes that
// connect, order, emphasize or explain (never decoration); dark covers, light interiors.
//
// Today only the «Cuaderno de trabajo» (PDF) uses it. The earlier templates keep their own identity (the palette of
// the brand profile: forest, ivory, chocolate, mustard; Georgia/Arial/Montserrat) and never read this file.
// A piece records the identity key and version it was made with: a later version never changes a saved piece.
// ─────────────────────────────────────────────

export const IDENTITY_A = Object.freeze({
  key: 'pizarra',
  version: 1,
  label: 'Pizarra (dirección A)',
  approvedOn: '2026-11-04',
  colors: Object.freeze({
    carbon: '#1E2627',     // dark covers (chalkboard)
    petroleo: '#173A40',   // titles on light pages
    tiza: '#F4F1EA',       // text on dark
    tizaSoft: '#D6DEDC',   // secondary text on dark
    papel: '#FBF8F2',      // light pages (reading, exercises)
    panel: '#F3EEE3',      // boxes on light pages
    tinta: '#23302F',      // body text titles
    texto: '#33403F',      // paragraphs
    gris: '#5D6968',       // hints, footers
    linea: '#CFCBC2',      // writing lines
    turquesa: '#1FB5B0',
    turquesaTexto: '#16807C', // turquoise for small text on light pages (contrast)
    coral: '#EE6352',
    coralTexto: '#C9442F',
    amarillo: '#F2C230',
    amarilloTexto: '#A87B00',
  }),
  // Font files ship with Oike (SIL OFL, public/fonts/pizarra). The same files are measured for the layout, drawn in
  // the preview and embedded in the PDF, so line breaks are identical everywhere.
  fonts: Object.freeze({
    title: { file: 'caveat-brush-400.ttf', family: 'OikePizarraTitle', weight: 400, use: 'Títulos y destacados' },
    note: { file: 'caveat-600.ttf', family: 'OikePizarraNote', weight: 600, use: 'Anotaciones breves' },
    body: { file: 'nunito-sans-400.ttf', family: 'OikePizarraBody', weight: 400, use: 'Párrafos e instrucciones' },
    bodyBold: { file: 'nunito-sans-700.ttf', family: 'OikePizarraBodyBold', weight: 700, use: 'Preguntas y pies de página' },
    bodyHeavy: { file: 'nunito-sans-800.ttf', family: 'OikePizarraBodyHeavy', weight: 800, use: 'Etiquetas' },
  }),
  fontDir: 'fonts/pizarra',
  texture: 'fonts/pizarra/chalk.png', // subtle chalk specks on dark covers only
  // Accents cycle in this order (steps 1, 2, 3; notes)
  accents: ['turquesa', 'coral', 'amarillo'],
  rules: Object.freeze([
    'Portadas intensas y oscuras; interiores claros para leer y escribir.',
    'Letra manuscrita solo en títulos y anotaciones breves; párrafos en letra sencilla.',
    'Cada trazo cumple una función: conectar, ordenar, enfatizar o explicar.',
    'La imagen tiene su zona y el texto la suya: nunca se superponen.',
    'El texto nunca se achica para entrar: pasa a la página siguiente.',
  ]),
});

export const BRAND_IDENTITIES = Object.freeze({ [IDENTITY_A.key]: IDENTITY_A });

/** The identity a piece was made with (key and version); unknown keys fall back to null (never guessed). */
export function identityOf(ref) {
  const id = BRAND_IDENTITIES[ref?.key];
  return id && Number(ref?.version || 1) === id.version ? id : null;
}

/** URL of an identity asset, relative to the site root (or to the page when opened from a file, as the demo). */
export function identityAssetUrl(path) {
  if (typeof window !== 'undefined' && window.location?.protocol === 'file:') return path;
  return `/${path}`;
}
